// src/pages/FraudDashboard.js
// Fraud Management → Dashboard page
// Layout: Sidebar + Navbar (existing components) + all fraud widgets

import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import DashboardNavbar from '../components/DashboardNavbar';
import useFraudDashboard from '../hooks/useFraudDashboard';

// ── Fraud Widgets (each is an independent, replaceable module) ─────────────
import FraudSummaryCards from '../components/fraud/FraudSummaryCards';
import FraudCharts from '../components/fraud/FraudCharts';
import FraudEventsTable from '../components/fraud/FraudEventsTable';
import { FraudRiskUsers } from '../components/fraud/FraudRiskUsers';
import FraudDestinations from '../components/fraud/FraudDestinations';
import FraudSystemHealth from '../components/fraud/FraudSystemHealth';

// ─── Error Banner ─────────────────────────────────────────────────────────────
const ErrorBanner = ({ message }) => (
  <div
    style={{
      background: 'rgba(239,68,68,0.12)',
      border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 8,
      padding: '12px 16px',
      color: '#EF4444',
      fontSize: 13,
      margin: '0 24px 16px',
    }}
  >
    ⚠️ {message}
  </div>
);

// ─── Main Page ─────────────────────────────────────────────────────────────────
const FraudDashboard = () => {
  const {
    stats,
    recentEvents,
    topRiskUsers,
    topDestinations,
    trendData,
    actionBreakdown,
    systemHealth,
    loading,
    error,
  } = useFraudDashboard();

  return (
    <div className="d-flex entire-bg-color">
      <Sidebar />

      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <DashboardNavbar />

        {/* ── Summary Cards ── */}
        <FraudSummaryCards stats={stats} loading={loading} />

        {/* ── Error banner (only if Firestore fetch failed) ── */}
        {error && <ErrorBanner message={error} />}

        {/* ── Charts ── */}
        <FraudCharts
          trendData={trendData}
          actionBreakdown={actionBreakdown}
          loading={loading}
        />

        {/* ── Recent Events Table ── */}
        <FraudEventsTable events={recentEvents} loading={loading} />

        {/* ── Bottom row: Risk Users + Destinations + System Health ── */}
        <Container fluid className="px-4 my-4">
          <Row className="g-3">
            {/* Top Risk Users */}
            <Col xs={12} lg={5}>
              <FraudRiskUsers users={topRiskUsers} loading={loading} />
            </Col>

            {/* Top Destinations */}
            <Col xs={12} lg={4}>
              <FraudDestinations destinations={topDestinations} loading={loading} />
            </Col>

            {/* System Health */}
            <Col xs={12} lg={3}>
              <FraudSystemHealth health={systemHealth} loading={loading} />
            </Col>
          </Row>
        </Container>

        {/* ── Bottom spacer ── */}
        <div style={{ height: 40 }} />
      </div>
    </div>
  );
};

export default FraudDashboard;
